/**
 * 교사대학(내 학습) 강의 상태
 * - 판정 기준은 "수강 기간(remainDays)"이 우선이며, isRegistered 는 remainDays 정보가
 *   없을 때(null) 수강전/기간종료를 가르는 용도로만 사용한다.
 *
 * - 수강전 : remainDays == null && isRegistered = false (아직 수강 기간이 시작되지 않음)
 * - 이용가능(수강중) : remainDays > 0 || (remainDays == 0 && endDateToday)
 *                      → 잔여일이 카운트다운 중이면 이미 수강이 시작된 것으로 보고 입장 허용
 * - 기간종료 : (remainDays == null && isRegistered = true) || (remainDays <= 0 && !endDateToday)
 *
 * 학습 시작은 [ 수강전 ] 상태에서만 가능
 * 학습 연장은 [ 이용가능, 기간종료 ] 상태에서 가능 (이용가능은 만료 30일 이하일 때만 노출)
 * 학습 진행은 [ 이용가능 ] 상태에서만 가능
 */

import { useNavigate } from 'react-router-dom';
import { Button } from '@material-tailwind/react';
import TextWinds from '../../../../components/common/TextWinds';
import ProgressDefault from './ProgressDefault';
import { calcPercent, changeTimestamp } from '../../../../api/utils/util-func';
import LearningCardShell, {
  CardEnrolment,
  ExtendButton,
  isEndDateToday,
} from './LearningCardShell';

export interface MyLearningType {
  id: string; // 학습 ID
  name: string; // 학습 이름
  thumbnail: string; // 썸네일
  isRegistered: boolean; // 학습 동의(시작) 여부
  enrolment: CardEnrolment;
  createdAt: string; // 학습 생성일
  updatedAt: string; // 학습 수정일
}

interface MyLearningCardProps {
  data: MyLearningType;
  linkTo: string;
  openStart: (id?: string) => void;
  openExtend: (id?: string) => void;
}

type LearningStatus = '수강전' | '이용가능' | '기간종료';

/** 내 학습 - 목록 내 카드 */
const MyLearningCard = ({
  data,
  linkTo,
  openStart,
  openExtend,
}: MyLearningCardProps) => {
  const navigate = useNavigate();
  const { enrolment } = data;
  const endDateToday = isEndDateToday(enrolment?.endDate);

  // 학습 상태 확인
  const getStatus = (): LearningStatus => {
    // 잔여일 정보가 없으면: 등록 이력이 있으면 기간종료, 없으면 아직 시작 전(수강전)
    if (enrolment?.remainDays == null) {
      return data.isRegistered ? '기간종료' : '수강전';
    }
    // 기간이 만료됐고 종료 당일도 아니면: 등록 여부와 무관하게 기간종료
    if (Number(enrolment.remainDays) <= 0 && !endDateToday) {
      return '기간종료';
    }
    // 잔여일이 카운트다운 중 = 이미 수강이 시작됨 → isRegistered 와 무관하게 이용가능
    return '이용가능';
  };

  const status = getStatus();

  // '기간 연장하기' 버튼 노출 여부 (학습 상태 기준으로 판정)
  // 1. 기간종료 : 항상 노출
  // 2. 수강 기간 정보 없음(remainDays null, 수강전) : 연장 비노출
  // 3. 이용가능 : 만료 임박(30일 이하)일 때만 연장 노출
  const canExtend = () => {
    if (status === '기간종료') {
      return true;
    }
    if (enrolment?.remainDays == null) {
      return false;
    }
    return Number(enrolment.remainDays) <= 30;
  };

  // 버튼 클릭 - 상태에 따라 시작 모달 / 페이지 이동 / 연장 모달
  const handleActionClick = () => {
    switch (status) {
      case '수강전':
        openStart(data.id);
        break;
      case '이용가능':
        navigate(`${linkTo}`);
        break;
      case '기간종료':
        openExtend(data.id);
        break;
    }
  };

  // '기간 연장하기' 버튼 클릭 - 연장 모달
  const handleExtendClick = () => {
    openExtend(data.id);
  };

  // 학습 기간 존재 여부 확인 (endDate가 있고, remainDays가 null이 아닌 경우)
  const hasPeriod = enrolment?.endDate && enrolment?.remainDays !== null;

  return (
    <LearningCardShell thumbnail={data.thumbnail} name={data.name}>
      {hasPeriod ? (
        <div className="flex flex-col gap-y-2">
          <ProgressDefault
            percentage={calcPercent(
              new Date(enrolment.startDate),
              new Date(enrolment.endDate),
            )}
          />
          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
            <TextWinds
              type="content_body4"
              color="gray7"
              className="whitespace-nowrap"
            >
              {Number(enrolment.remainDays) > 0
                ? `${enrolment.remainDays}일 남음 `
                : endDateToday
                  ? '오늘까지 '
                  : '기간 종료 '}
              ({changeTimestamp(enrolment.endDate)}까지)
            </TextWinds>
            {canExtend() && <ExtendButton onClick={handleExtendClick} />}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-y-2">
          <ProgressDefault percentage={0} />
          {canExtend() ? (
            <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
              <TextWinds
                type="content_body4"
                color="gray7"
                className="whitespace-nowrap"
              >
                기간 종료
              </TextWinds>
              <ExtendButton onClick={handleExtendClick} />
            </div>
          ) : (
            <TextWinds type="content_body4" color="gray7">
              학습 시작 전
            </TextWinds>
          )}
        </div>
      )}

      <Button
        className={`rounded p-2 shadow-none hover:shadow-none ${
          status === '기간종료'
            ? 'bg-gray5 hover:bg-gray6'
            : 'bg-purple5 hover:bg-purple6'
        }`}
        disabled={status === '기간종료'}
        onClick={handleActionClick}
      >
        <TextWinds type="button" color="white">
          {status === '수강전' ? '학습 시작하기' : '학습 입장하기'}
        </TextWinds>
      </Button>
    </LearningCardShell>
  );
};

export default MyLearningCard;
